import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import {
    useSessionHistory,
    type SessionHistoryAction,
    type SessionHistoryChange,
    type SessionHistoryEntry,
} from '@/api/sessions';
import { useActiveSessionTypes, useActiveCancelReasons } from '@/api/referenceData';
import { useUserDirectory } from '@/api/users';

/*
 * SessionHistoryPanel — read-only timeline of detail-tab changes for one
 * session. Backed by sessions.change_log on the server (see migration
 * 106 and src/services/sessionChangeLog.js). Newest entries first.
 *
 * UUID resolution: the change_log stores raw FK values for
 * session_type_id, supervising_bcba_id, and cancel_reason_id. We resolve
 * them to display names via the same reference-data hooks the edit form
 * uses, so a renamed session-type or deactivated BCBA still renders
 * sensibly. Unresolvable IDs fall through as the bare UUID — visible
 * enough for forensic follow-up but not pretty.
 */

const ACTION_LABELS: Record<SessionHistoryAction, string> = {
    created: 'Session created',
    updated: 'Updated',
    completed: 'Marked complete',
    cancelled: 'Cancelled',
    promoted_to_series: 'Promoted to recurring series',
    series_updated: 'Updated as part of series edit',
};

const FIELD_LABELS: Record<string, string> = {
    client:                 'Client',
    staff:                  'Staff',
    session_type_id:        'Session type',
    location:               'Location',
    date:                   'Date',
    start_h:                'Start hour',
    start_m:                'Start minute',
    dur:                    'Duration',
    status:                 'Status',
    notes:                  'Notes',
    label:                  'Label',
    is_supervised:          'Supervision',
    supervising_bcba_id:    'Supervising BCBA',
    cancel_reason_id:       'Cancel reason',
};

function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

function formatDuration(minutes: number): string {
    if (minutes <= 0) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(' ');
}

interface Resolver {
    sessionType: (id: string) => string;
    bcba: (id: string) => string;
    cancelReason: (id: string) => string;
}

function formatValue(field: string, value: unknown, resolver: Resolver): string {
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'is_supervised') return value ? 'Yes' : 'No';
    if (field === 'dur' && typeof value === 'number') return formatDuration(value);
    if (field === 'session_type_id' && typeof value === 'string') return resolver.sessionType(value);
    if (field === 'supervising_bcba_id' && typeof value === 'string') return resolver.bcba(value);
    if (field === 'cancel_reason_id' && typeof value === 'string') return resolver.cancelReason(value);
    return String(value);
}

function fieldLabel(field: string): string {
    return FIELD_LABELS[field] || field;
}

export function SessionHistoryPanel({ sessionId }: { sessionId: string }) {
    const { data, isLoading, isError } = useSessionHistory(sessionId);
    const sessionTypes = useActiveSessionTypes();
    // SessionPage already pulls the full directory; derive BCBAs from it
    // rather than firing a second filtered query against the same data.
    // supervising_bcba_id is by definition a BCBA; a BCBA who's since been
    // deactivated falls through to their UUID.
    const staffDirectory = useUserDirectory();
    const bcbas = useMemo(
        () => (staffDirectory.data ?? []).filter((u) => u.roleType === 'BCBA'),
        [staffDirectory.data],
    );
    const cancelReasons = useActiveCancelReasons();

    const resolver = useMemo<Resolver>(() => ({
        sessionType: (id) =>
            sessionTypes.data?.find((t) => t.id === id)?.name ?? id,
        bcba: (id) => {
            const u = bcbas.find((x) => x.id === id);
            return u ? `${u.first} ${u.last}` : id;
        },
        cancelReason: (id) =>
            cancelReasons.data?.find((r) => r.id === id)?.text ?? id,
    }), [sessionTypes.data, bcbas, cancelReasons.data]);

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-text-muted">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
            </div>
        );
    }
    if (isError) {
        return <Alert variant="danger">Couldn't load session history.</Alert>;
    }

    const entries = (data ?? []);
    if (entries.length === 0) {
        return (
            <Card>
                <div className="text-sm text-text-muted">
                    No detail-tab changes recorded yet.
                </div>
            </Card>
        );
    }

    // Newest first — the column stores them in append order (oldest first).
    const ordered = [...entries].reverse();

    return (
        <Card>
            <ol className="space-y-5">
                {ordered.map((entry, idx) => (
                    <HistoryEntry
                        key={`${entry.at}-${idx}`}
                        entry={entry}
                        resolver={resolver}
                    />
                ))}
            </ol>
        </Card>
    );
}

function HistoryEntry({
    entry, resolver,
}: { entry: SessionHistoryEntry; resolver: Resolver }) {
    const actor = entry.by?.userName || 'Unknown user';
    const actionLabel = ACTION_LABELS[entry.action] || entry.action;

    return (
        <li className="border-l-2 border-border pl-4">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium text-text">{actionLabel}</span>
                <span className="text-xs text-text-muted">by {actor}</span>
                <span className="text-xs text-text-subtle ml-auto">{formatTimestamp(entry.at)}</span>
            </div>
            {entry.changes && entry.changes.length > 0 && (
                <ul className="mt-2 space-y-1">
                    {entry.changes.map((change, i) => (
                        <ChangeRow key={i} change={change} resolver={resolver} />
                    ))}
                </ul>
            )}
        </li>
    );
}

function ChangeRow({
    change, resolver,
}: { change: SessionHistoryChange; resolver: Resolver }) {
    const from = formatValue(change.field, change.from, resolver);
    const to = formatValue(change.field, change.to, resolver);
    return (
        <li className="text-sm">
            <span className="text-text-muted">{fieldLabel(change.field)}: </span>
            <span className="text-text">{from}</span>
            <span className="text-text-subtle"> → </span>
            <span className="text-text">{to}</span>
        </li>
    );
}
